import { processCharge } from '@/services/charge'
import { applyDiscount, removeDiscount } from '@/services/discount'
import {
  changeDate,
  getNextOrder,
  type Charge,
  type ChargeLineItem,
} from '@/services/order'
import { defineStore } from 'pinia'
import { useFutureOrderStore } from './futureOrder'
import { AsyncOperationState } from './lib/AsyncOperationState'
import { useOnetimeStore } from './onetime'
import { useOrderStore } from './order'
import { useProductImageStore } from './productImage'
import { useSubscriptionStore } from './subscription'

import Bugsnag from '@bugsnag/js'

export const useNextOrderStore = defineStore('nextOrder', {
  state: () => ({
    loadState: AsyncOperationState.IDLE,
    workState: AsyncOperationState.IDLE,
    discountState: AsyncOperationState.IDLE,
    order: null as Charge | null,
    stale: true as boolean,
  }),
  getters: {
    getItemById() {
      return (id: string) => {
        return this.order?.line_items.find(
          (item) => `${item.purchase_item_id}` === id,
        )
      }
    },
  },
  actions: {
    async init(rechargeId?: string) {
      if (
        (this.stale || this.loadState === AsyncOperationState.IDLE) &&
        rechargeId
      ) {
        await this.load(rechargeId)
      }
    },
    resetWorkState() {
      this.workState = AsyncOperationState.IDLE
    },
    async load(rechargeId: string) {
      const productImageStore = useProductImageStore()
      this.loadState = AsyncOperationState.IN_PROGRESS
      try {
        const order = await getNextOrder(rechargeId)
        if (order) {
          productImageStore.loadImagesForProducts(
            order.line_items.map(
              (item: ChargeLineItem) =>
                `${item.external_product_id.ecommerce}:${item.external_variant_id.ecommerce}`,
            ),
          )
        }
        this.order = order
        this.stale = false
        this.loadState = AsyncOperationState.DONE
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.loadState = AsyncOperationState.ERRORED
      }
    },
    async changeDate(rechargeId: string, from: string, to: string) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        await changeDate(rechargeId, from, to)
        this.workState = AsyncOperationState.DONE

        // invalidate the stores that will need updating after this
        const subscriptionStore = useSubscriptionStore()
        const futureOrderStore = useFutureOrderStore()
        const onetimeStore = useOnetimeStore()

        subscriptionStore.stale = true
        onetimeStore.stale = true
        futureOrderStore.stale = true

        this.load(rechargeId)
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async processCharge(rechargeId: string): Promise<Charge | null> {
      this.workState = AsyncOperationState.IN_PROGRESS
      if (!this.order) {
        this.workState = AsyncOperationState.ERRORED
        return null
      }
      try {
        const { charge } = await processCharge(rechargeId, this.order.id)
        this.workState = AsyncOperationState.DONE

        // invalidate the stores that will need updating after this
        const subscriptionStore = useSubscriptionStore()
        const futureOrderStore = useFutureOrderStore()
        const onetimeStore = useOnetimeStore()
        const orderStore = useOrderStore()

        subscriptionStore.stale = true
        onetimeStore.stale = true
        futureOrderStore.stale = true
        orderStore.stale = true

        this.load(rechargeId)
        return charge
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
        return null
      }
    },
    async applyDiscount(
      rechargeId: string,
      discountCode: string,
      addressId?: string,
    ) {
      const resolvedAddressId = addressId ?? this.order?.address_id.toString()
      if (!resolvedAddressId) return
      try {
        this.discountState = AsyncOperationState.IN_PROGRESS
        await applyDiscount(rechargeId, resolvedAddressId, discountCode)
        return new Promise((resolve) =>
          setTimeout(() => {
            this.discountState = AsyncOperationState.DONE
            this.load(rechargeId)
            resolve(true)
          }, 5000),
        )
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.discountState = AsyncOperationState.ERRORED
      }
    },
    async removeDiscount(rechargeId: string, addressId?: string) {
      const resolvedAddressId = addressId ?? this.order?.address_id.toString()
      if (!resolvedAddressId) return
      this.discountState = AsyncOperationState.IN_PROGRESS
      await removeDiscount(rechargeId, resolvedAddressId)
      this.discountState = AsyncOperationState.DONE
      this.load(rechargeId)
    },
  },
})
